/* eslint-disable import/first */

/**
 * DEFINE GLOBAL
 *
 * Needed to enable aspects of the Chime SDK to function properly, e.g. CSPMonitor.
 *
 * Declared here instead of Vite config due to conflicts with 'global' definitions
 * within other files e.g. Chakra UI styles
 *
 */
window.global ||= window

import { selectAuthEnvironment, SharedContext, useSelector } from '@missionlabs/api'
import { FeatureFlagProvider } from '@missionlabs/feature-flags'
import { ErrorBoundary, ThemeProvider, Toaster } from '@missionlabs/react'
import { getConfigCatSdkKey, getDestinationOSName } from '@missionlabs/utils'
import isElectron from 'is-electron'
import { KBarProvider } from 'kbar'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HeadProvider } from 'react-head'
import { I18nextProvider } from 'react-i18next'
import { Provider as ReduxProvider } from 'react-redux'
import { BrowserRouter, HashRouter } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { AppRouter } from 'routes'
import { KeycloakProvider } from 'shared/auth/KeycloakProvider'
import { ErrorFallback } from 'shared/components/ErrorFallback'
import { persistor, store } from 'shared/store'
import { AppConfig } from 'shared/utils/env'

import { CommandMenu } from './shared/components/CommandMenu/CommandMenu'
import i18n from './translations'

if (
    import.meta.env.NODE_ENV === 'development' &&
    import.meta.env.REACT_APP_MOCKING_ENABLED === 'true'
) {
    import('@missionlabs/api').then(({ createWorker }) => {
        createWorker().then(worker => worker!.start({ onUnhandledRequest: 'bypass' }))
    })
}

const Router = isElectron() ? HashRouter : BrowserRouter

function openDebugOnQueryChange(query: string) {
    if (!isElectron()) return
    if (query.toLowerCase() === 'debug') {
        window.openDebug()
    }
}

const platform = isElectron() ? getDestinationOSName(window.platform) : 'web'

const AppProviders = () => {
    const { environment } = useSelector(selectAuthEnvironment)

    return (
        <FeatureFlagProvider
            sdkKey={getConfigCatSdkKey(environment)}
            attributes={{
                brand: AppConfig.brand,
                platform,
                version: __VERSION__
            }}
        >
            <I18nextProvider i18n={i18n}>
                <HeadProvider>
                    <ThemeProvider variant={AppConfig.theme}>
                        <ErrorBoundary fallback={ErrorFallback}>
                            <Toaster />
                            <Router>
                                <KeycloakProvider>
                                    <KBarProvider
                                        options={{
                                            callbacks: {
                                                onQueryChange: openDebugOnQueryChange
                                            }
                                        }}
                                    >
                                        <CommandMenu />
                                        <AppRouter />
                                    </KBarProvider>
                                </KeycloakProvider>
                            </Router>
                        </ErrorBoundary>
                    </ThemeProvider>
                </HeadProvider>
            </I18nextProvider>
        </FeatureFlagProvider>
    )
}

createRoot(document.getElementById('root') as HTMLElement).render(
    <StrictMode>
        <ReduxProvider store={store} context={SharedContext}>
            <PersistGate loading={null} persistor={persistor}>
                <AppProviders />
            </PersistGate>
        </ReduxProvider>
    </StrictMode>
)
